import {
  createContext,
  useContext,
  createSignal,
  createEffect,
  createMemo,
  on,
  onCleanup,
} from 'solid-js';
import { unwrap } from 'solid-js/store';
import { getSchemaValidator } from 'schemas';
import { diffSnapshotEntries, sameSnapshotEntries } from './snapshot-diff';
import { planLayerApply } from './layer-update-plan';

const LayerContext = createContext({});
const useLayers = () => useContext(LayerContext);

interface LayerInstance {
  id: string;
  linked?: string;
  exclusiveOf?: string[];
}

export type LayerInstanceMap = Record<string, LayerInstance>;

const getRootLayerId = (
  instances: LayerInstanceMap,
  id: string,
  visited: Set<string> = new Set(),
): string => {
  if (visited.has(id)) {
    console.warn(
      `Cycle detected in linked layers: ${[...visited, id].join(' -> ')}`,
    );
    return id;
  }
  const layer = instances[id];
  if (layer?.linked && instances[layer.linked]) {
    visited.add(id);
    return getRootLayerId(instances, layer.linked, visited);
  }
  return id;
};

export const resolveVisibleLayers = (
  instances: LayerInstanceMap,
  requestedIds: string[],
): string[] => {
  const requestedRoots = requestedIds.filter(
    (id) => instances[id] && !instances[id].linked,
  );

  // EID-99: getRootLayerId walks the `linked` chain recursively; memoize it
  // per call so neither the compatibility loop nor the linked-group expansion
  // below re-walks the same chains repeatedly.
  const rootCache = new Map<string, string>();
  const rootOf = (id: string): string => {
    let root = rootCache.get(id);
    if (root === undefined) {
      root = getRootLayerId(instances, id);
      rootCache.set(id, root);
    }
    return root;
  };

  // Reverse index of exclusiveOf: id -> set of ids that exclude it.
  // requestedRoots/visibleRoots only ever contain non-linked ids, whose own
  // root is themselves, so two such ids are incompatible exactly when either
  // names the other in its exclusiveOf list. That lets a newly added id's
  // incompatibilities be read off its own (small) exclusiveOf list plus this
  // reverse index in O(1) per entry, instead of re-testing it against every
  // currently visible id (the former requestedRoots × visibleRoots scan).
  const excludedBy = new Map<string, Set<string>>();
  for (const [layerId, layer] of Object.entries(instances)) {
    for (const excludedId of layer.exclusiveOf || []) {
      let excluders = excludedBy.get(excludedId);
      if (!excluders) excludedBy.set(excludedId, (excluders = new Set()));
      excluders.add(layerId);
    }
  }

  const visibleRoots = new Set<string>();

  for (const id of requestedRoots) {
    const layer = instances[id];
    const incompatible = new Set<string>();
    for (const excludedId of layer.exclusiveOf || []) {
      if (excludedId !== id && visibleRoots.has(excludedId)) {
        incompatible.add(excludedId);
      }
    }
    for (const excluderId of excludedBy.get(id) ?? []) {
      if (excluderId !== id && visibleRoots.has(excluderId)) {
        incompatible.add(excluderId);
      }
    }
    incompatible.forEach((incompatibleId) =>
      visibleRoots.delete(incompatibleId),
    );
    visibleRoots.add(id);
  }

  // Expand each visible root back out to its linked group in one pass over
  // `instances`, instead of re-scanning all of `instances` per visible root.
  const groupByRoot = new Map<string, string[]>();
  for (const instanceId of Object.keys(instances)) {
    const root = rootOf(instanceId);
    let group = groupByRoot.get(root);
    if (!group) groupByRoot.set(root, (group = []));
    group.push(instanceId);
  }

  const finalVisible = new Set<string>();
  for (const rootId of visibleRoots) {
    // Emit the root first, then its linked followers in instances order —
    // this reproduces the old getLinkedGroup() ordering exactly (root-first),
    // which callers rely on for layer draw/z-order.
    finalVisible.add(rootId);
    for (const memberId of groupByRoot.get(rootId) ?? [rootId]) {
      if (memberId !== rootId) finalVisible.add(memberId);
    }
  }

  return Array.from(finalVisible);
};

const LayerProvider = (props) => {
  const [layerInstances, setLayerInstances] = createSignal({});
  const [legendLayers, setLegendLayers] = createSignal([]);
  const [activeLayer, setActiveLayer] = createSignal(null);
  const [visibleLayers, _setVisibleLayers] = createSignal([]);

  const layerLoader = async (lyrspec) => {
    const layerType = lyrspec.layerSpec.layerType;
    const layermod = await import(
      `../../components/worldlayers/${layerType}/src/index.ts`
    );
    const schema = await import(
      `../../components/worldlayers/${layerType}/schemas/layerspec.json`
    );
    const schemaValidator = await getSchemaValidator();
    // The shared Ajv instance pre-registers every worldlayer schema by $id, so
    // re-compiling the layer's own layerspec.json (same $id) throws "already exists".
    // Reuse the already-registered schema when present (issue #28).
    const schemaId = (schema as { $id?: string }).$id;
    const validate = schemaId
      ? (schemaValidator.getSchema(schemaId) ?? schemaValidator.compile(schema))
      : schemaValidator.compile(schema);
    if (validate(lyrspec.layerSpec)) {
      console.log('Loaded layer ' + lyrspec.id);
      const instance = new layermod.default(lyrspec);
      if (typeof instance.init === 'function') {
        await instance.init();
      }
      return instance;
    } else {
      validate.errors.map((error) =>
        console.log(
          `Spec for layer ${lyrspec.id} does not validate: ${error.instancePath} -> ${error.message} [${JSON.stringify(error.params)}]`,
        ),
      );
      return null;
    }
  };

  // EID-61: instead of deep-tracking every layer property and reloading ALL
  // layers (dynamic imports + schema compile + Handlebars/colormap rebuild +
  // instance.init) on any patch, serialise each layer spec to a snapshot.
  // JSON.stringify reads every nested property through the store proxy, so
  // the memo still observes any deep change (including remote Yjs patches),
  // but the snapshot strings let the effect below reload only the layers
  // whose content actually changed. sameSnapshotEntries absorbs upstream
  // re-evaluations that produced identical content.
  const layerEntries = createMemo(
    () =>
      (props.layers ?? []).map((lyrspec) => ({
        id: lyrspec.id,
        snapshot: JSON.stringify(lyrspec),
      })),
    undefined,
    { equals: sameSnapshotEntries },
  );

  // Snapshots of the layer set the instance registry currently reflects.
  // Updated only when a load pass actually applies, so a superseded (stale)
  // pass leaves its changes pending for the next pass to pick up.
  let appliedSnapshots: Record<string, string> = {};

  createEffect(
    on(layerEntries, (entries) => {
      const { changedIds, removedIds, nextById } = diffSnapshotEntries(
        appliedSnapshots,
        entries,
      );
      if (!changedIds.length && !removedIds.length) return;
      // Stale-load guard (EID-61): the previous implementation returned a
      // React-style cleanup function, which Solid ignores — overlapping async
      // loads could apply stale layer sets out of order. onCleanup runs when
      // the effect re-runs or the provider disposes; only the latest pass may
      // apply its results.
      let stale = false;
      onCleanup(() => {
        stale = true;
      });
      const specsById = new Map(
        (props.layers ?? []).map((lyrspec) => [lyrspec.id, lyrspec]),
      );
      const loads = changedIds.map((id) =>
        layerLoader(unwrap(specsById.get(id))),
      );
      Promise.all(loads).then((loaded) => {
        if (stale) return;
        const loadedById = new Map(
          changedIds.map((id, index) => [id, loaded[index]]),
        );
        const {
          instances,
          legendLayers: nextLegendLayers,
          requestedVisibleIds,
        } = planLayerApply({
          orderedIds: entries.map((entry) => entry.id),
          changedIds,
          loadedById,
          prevInstances: layerInstances(),
          currentVisibleIds: visibleLayers(),
        });
        for (const lyr of Object.values(instances)) {
          if (lyr.linked && !instances[lyr.linked]) {
            console.warn(
              `Layer "${lyr.id}" declares linked layer "${lyr.linked}" which does not exist`,
            );
          }
        }
        appliedSnapshots = nextById;
        setLayerInstances(instances);
        setLegendLayers(nextLegendLayers);
        setVisibleLayers(requestedVisibleIds);
      });
    }),
  );

  const setVisibleLayers = (requestedVisibleIdsArg) => {
    const requestedIds = [...unwrap(requestedVisibleIdsArg)];
    _setVisibleLayers(resolveVisibleLayers(layerInstances(), requestedIds));
  };

  const zoomVisibility = (id, zoom) => {
    const layer = layerInstances()[id];
    const hidden = zoom < layer.minZoom || zoom > layer.maxZoom;
    return !hidden;
  };

  return (
    <LayerContext.Provider
      value={{
        layerInstances,
        legendLayers,
        activeLayer,
        setActiveLayer,
        visibleLayers,
        setVisibleLayers,
        zoomVisibility,
      }}
    >
      {props.children}
    </LayerContext.Provider>
  );
};

export { useLayers, LayerProvider };
